import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, map } from 'rxjs';

import { StreamData } from '../shared/stream.token';
import { VideoLayoutService } from '../shared/video-layout.service';

import { RaceSessionService } from '../timing-tower/race-session.service';
import { TimingTowerService } from '../timing-tower/timing-tower.service';
import { VbLxpVideoPlayerComponent } from '../video/video-player/vb-lxp-video-player.component';

export type SlotItem =
	| { type: 'timing'; id: string; streamData: StreamData }
	| { type: 'mainStream' }
	| { type: 'twitchStream' };

@Injectable({
	providedIn: 'root',
})
export class LivestreamViewService {
	private destroyRef = inject(DestroyRef);
	public timingTowerService = inject(TimingTowerService);
	public layoutService = inject(VideoLayoutService);
	public raceSessionService = inject(RaceSessionService);

	public isMainStreamSelected = signal(true);
	public isTwitchStreamSelected = signal(false);

	private mainStreamData: StreamData | null = null;
	public raceStreamData = [];
	public slotStreamData = [];
	public currentPlayers: VbLxpVideoPlayerComponent[] = [];

	public occupiedSlots$ = new BehaviorSubject<SlotItem[]>([]);
	public isNoPlayerSelected = signal(true);
	public slotStreamData$ = this.occupiedSlots$.pipe(
		map((slots) => {
			return slots.map((s) => {
				switch (s.type) {
					case 'timing':
						return this.raceStreamData.find((rsd) => rsd.carNumber === s.id);
					case 'mainStream':
						return this.getMainStreamData();
					case 'twitchStream':
						return this.getTwitchStreamData();
				}
			});
		}),
	);

	constructor() {
		// Subscribe to main-stream updates from RaceSessionService
		this.raceSessionService.mainStream$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((mainStreamData) => {
				this.mainStreamData = mainStreamData;
				this.ensureMainStreamSlotIfReady();
				this.refreshMainStreamPlayersIfPresent();
			});

		this.raceSessionService.raceStreamData$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((raceStreamData) => {
				this.raceStreamData = raceStreamData;
			});

		this.slotStreamData$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((streamData) => {
				this.slotStreamData = streamData;
			});

		// Check on layout change for pruning logic
		this.layoutService.activeLayout$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				const maxSelectable = this.layoutService.getPlayerCount();

				const overflow = this.occupiedSlots$.value.length - maxSelectable;
				if (overflow <= 0) {
					return;
				}
				const removed = this.pruneOldest(overflow);

				for (const slot of removed) {
					switch (slot.type) {
						case 'timing':
							this.timingTowerService.setInactive(slot.id);
							break;
						case 'mainStream':
							this.isMainStreamSelected.set(false);
							break;
					}
				}
			});

		// Check if slots overflow max selectable per layout and remove oldest
		this.occupiedSlots$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((slots: SlotItem[]) => {
				for (let i = 0; i < this.currentPlayers.length; i++) {
					const currentPlayer = this.currentPlayers[i];
					const currentStreamData = currentPlayer.streamData;
					if (!this.slotStreamData.includes(currentStreamData)) {
						const newStreamData = this.slotStreamData.filter(
							(sd) => !this.currentPlayers.map((p) => p.streamData).includes(sd),
						);
						currentPlayer.setStreamData(newStreamData[0]);
					}
				}

				this.enforceSelectionLimit(slots);
				this.isNoPlayerSelected.set(slots.length === 0);
			});
	}

	public addSlotItem(slotItem: SlotItem) {
		const slots = this.occupiedSlots$.value;
		this.occupiedSlots$.next([...slots, slotItem]);
	}

	public removeSlotItem(slotItem: SlotItem) {
		const slots = this.occupiedSlots$.value;
		if (slotItem.type === 'timing') {
			this.occupiedSlots$.next(
				slots.filter((s) => {
					if (s.type === 'timing') {
						return s.id !== slotItem.id;
					}
					return true;
				}),
			);
		} else {
			this.occupiedSlots$.next(slots.filter((s) => s.type !== slotItem.type));
		}
	}

	public pruneOldest(overflow: number): SlotItem[] {
		const current = this.occupiedSlots$.value;
		if (overflow <= 0 || current.length === 0) {
			return [];
		}

		const removeCount = Math.min(overflow, current.length);

		const removed = current.slice(0, removeCount);
		const remaining = current.slice(removeCount);

		this.occupiedSlots$.next(remaining);
		for (let slot of removed) {
			const player = this.currentPlayers.find((p) => {
				if (slot.type === 'timing') {
					return p.streamData === slot.streamData;
				} else if (slot.type === 'mainStream') {
					return p.streamData === this.getMainStreamData();
				} else {
					return p.streamData?.id === 'twitch-stream';
				}
			});
			if (player) {
				player.streamData = null;
			}
		}
		return removed;
	}

	public updateCurrentPlayers(players: VbLxpVideoPlayerComponent[]) {
		this.currentPlayers = players;
		for (let i = 0; i < this.occupiedSlots$.value.length; i++) {
			const currentSlot = this.occupiedSlots$.value[i];
			let streamData: StreamData;
			if (currentSlot.type === 'timing') {
				streamData = this.raceStreamData.find((rsd) => rsd.carNumber === currentSlot.id);
			} else if (currentSlot.type === 'mainStream') {
				streamData = this.getMainStreamData();
			} else if (currentSlot.type === 'twitchStream') {
				streamData = this.getTwitchStreamData();
			}
			this.currentPlayers[i].setStreamData(streamData);
		}
	}

	public enforceSelectionLimit(slots: SlotItem[]) {
		const maxSelectable = this.layoutService.getPlayerCount();

		const overflow = slots.length - maxSelectable;
		if (overflow <= 0) {
			return;
		}

		const removed = this.pruneOldest(overflow);

		for (const slot of removed) {
			switch (slot.type) {
				case 'timing':
					this.timingTowerService.setInactive(slot.id);
					break;
				case 'mainStream':
					this.isMainStreamSelected.set(false);
					break;
				case 'twitchStream':
					this.isTwitchStreamSelected.set(false);
					break;
			}
		}
	}

	public getMainStreamData() {
		return this.mainStreamData;
	}

	public getTwitchStreamData() {
		const streamData: StreamData = {
			id: 'twitch-stream',
			isLive: true,
		};
		return streamData;
	}

	public getStreamDataByCarNumber(carNumber: string): StreamData {
		return this.raceStreamData.find((sd) => sd.carNumber === carNumber);
	}

	private ensureMainStreamSlotIfReady(): void {
		if (
			this.getMainStreamData() &&
			this.isMainStreamSelected() &&
			!this.occupiedSlots$.value.some((slot) => slot.type === 'mainStream')
		) {
			this.addSlotItem({ type: 'mainStream' });
		}
	}

	private refreshMainStreamPlayersIfPresent(): void {
		if (!this.getMainStreamData()) {
			return;
		}

		const slots = this.occupiedSlots$.value;
		for (let i = 0; i < slots.length; i++) {
			if (slots[i].type !== 'mainStream') {
				continue;
			}

			const player = this.currentPlayers[i];
			if (player) {
				player.setStreamData(this.getMainStreamData());
			}
		}
	}

	public onMainStreamToggled(isSelected: boolean) {
		if (isSelected) {
			this.isMainStreamSelected.set(true);
			this.ensureMainStreamSlotIfReady();
		} else {
			this.removeSlotItem({ type: 'mainStream' });
			this.isMainStreamSelected.set(false);
		}
	}
}
